import type * as DocumentPicker from 'expo-document-picker';
import { ArrowLeft, ArrowRight, Upload, X } from 'lucide-react-native';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { FileUploadZone } from '../../components/FileUploadZone';
import { HStack, VStack } from '../../components/primitives';
import { Button } from '../../components/primitives/Button';
import { Sheet } from '../../components/primitives/Sheet';
import { Text } from '../../components/primitives/Text';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { useFunnelTemplates } from '../../hooks/useLeads';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';
import { ColumnMappingTable } from './components/ColumnMappingTable';
import { ImportPreviewTable } from './components/ImportPreviewTable';
import { ImportProgressCard } from './components/ImportProgressCard';
import { ImportSummaryCard } from './components/ImportSummaryCard';
import { TemplateDownloadButton } from './components/TemplateDownloadButton';
import { useExecuteImport, useUploadFile } from './hooks/useLeadImport';
import type { ColumnMapping, ImportResult, ImportStep } from './types';

type FileInput = File | DocumentPicker.DocumentPickerAsset;

interface SelectedFile {
  name: string;
  size?: number;
  raw: FileInput;
}

interface LeadImportContainerProps {
  visible: boolean;
  onClose: () => void;
}

export function LeadImportContainer({ visible, onClose }: Readonly<LeadImportContainerProps>) {
  const { t } = useTranslation();
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id;
  const { data: templates } = useFunnelTemplates(orgId);
  const activeTemplate = templates?.find((tpl: { isActive: boolean }) => tpl.isActive);

  const uploadFile = useUploadFile();
  const executeImport = useExecuteImport();
  const uploadResetRef = useRef(uploadFile.reset);
  const executeResetRef = useRef(executeImport.reset);
  uploadResetRef.current = uploadFile.reset;
  executeResetRef.current = executeImport.reset;

  const [step, setStep] = useState<ImportStep>('upload');
  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [previewRows, setPreviewRows] = useState<string[][]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [mappings, setMappings] = useState<ColumnMapping[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const resetState = useCallback(() => {
    setStep('upload');
    setSelectedFile(null);
    setFormData(null);
    setHeaders([]);
    setPreviewRows([]);
    setTotalRows(0);
    setMappings([]);
    setImportResult(null);
    uploadResetRef.current();
    executeResetRef.current();
  }, []);

  const handleClose = useCallback(() => {
    if (step === 'importing') return;
    resetState();
    onClose();
  }, [step, resetState, onClose]);

  const handleFileSelected = useCallback((file: FileInput) => {
    const fd = new FormData();

    if (Platform.OS === 'web') {
      const webFile = file as File;
      fd.append('file', webFile);
      setSelectedFile({ name: webFile.name, size: webFile.size, raw: file });
    } else {
      const nativeFile = file as DocumentPicker.DocumentPickerAsset;
      const formFile = { uri: nativeFile.uri, name: nativeFile.name, type: nativeFile.mimeType };
      fd.append('file', formFile as unknown as Blob);
      setSelectedFile({ name: nativeFile.name, size: nativeFile.size, raw: file });
    }

    setFormData(fd);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!formData || !orgId) return;

    try {
      const preview = await uploadFile.mutateAsync({ formData, organizationId: orgId });
      setHeaders(preview.headers);
      setPreviewRows(preview.rows);
      setTotalRows(preview.totalRows);
      setMappings(preview.mappings);
      setStep('mapping');
    } catch {
      // Error displayed via uploadFile.error
    }
  }, [formData, uploadFile, orgId]);

  const handleMappingChange = useCallback((index: number, crmField: string) => {
    setMappings((prev) =>
      prev.map((m, i) => (i === index ? { ...m, crmField, confidence: 1 } : m)),
    );
  }, []);

  const handleExecuteImport = useCallback(async () => {
    if (!orgId || !activeTemplate || !selectedFile) return;

    setStep('importing');

    try {
      const newFormData = new FormData();
      if (Platform.OS === 'web') {
        newFormData.append('file', selectedFile.raw as File);
      } else {
        const nativeFile = selectedFile.raw as DocumentPicker.DocumentPickerAsset;
        const formFile = { uri: nativeFile.uri, name: nativeFile.name, type: nativeFile.mimeType };
        newFormData.append('file', formFile as unknown as Blob);
      }

      const result = await executeImport.mutateAsync({
        organizationId: orgId,
        funnelTemplateId: activeTemplate.id,
        mappings: mappings.filter((m) => m.crmField !== 'skip'),
        file: newFormData,
      });
      setImportResult(result);
      setStep('complete');
    } catch {
      setStep('preview');
    }
  }, [orgId, activeTemplate, selectedFile, mappings, executeImport]);

  const stepTitle: Record<ImportStep, string> = useMemo(
    () => ({
      upload: t('leadImport.steps.upload'),
      mapping: t('leadImport.steps.mapping'),
      preview: t('leadImport.steps.preview'),
      importing: t('leadImport.steps.importing'),
      complete: t('leadImport.steps.complete'),
    }),
    [t],
  );

  return (
    <Sheet
      visible={visible}
      onClose={handleClose}
      position="center"
      size="md"
      hideClose
      bodyScrollable={false}
      disableBodyPadding
    >
      <View testID="lead_import_modal" style={styles.container}>
        <HStack justify="between" align="center" style={styles.header}>
          <HStack spacing="md" align="center">
            {step === 'mapping' && (
              <Pressable onPress={() => setStep('upload')} hitSlop={8}>
                <ArrowLeft size={20} color={colors.text.secondary} />
              </Pressable>
            )}
            {step === 'preview' && (
              <Pressable onPress={() => setStep('mapping')} hitSlop={8}>
                <ArrowLeft size={20} color={colors.text.secondary} />
              </Pressable>
            )}
            <Text variant="h5" color={colors.text.primary}>
              {stepTitle[step]}
            </Text>
          </HStack>
          {step !== 'importing' && (
            <Pressable onPress={handleClose} hitSlop={8} testID="close_import_button">
              <X size={20} color={colors.text.secondary} />
            </Pressable>
          )}
        </HStack>

        {step === 'mapping' ? (
          <View style={styles.bodyNoClip}>
            <VStack spacing="lg">
              <Text variant="body" color={colors.text.tertiary}>
                {t('leadImport.mappingHint')}
              </Text>
              <ColumnMappingTable mappings={mappings} onMappingChange={handleMappingChange} />
            </VStack>
          </View>
        ) : (
          <ScrollView
            style={styles.body}
            contentContainerStyle={styles.bodyContent}
            showsVerticalScrollIndicator={false}
          >
            {step === 'upload' && (
              <VStack spacing="lg">
                <FileUploadZone
                  onFileSelected={handleFileSelected}
                  selectedFile={selectedFile}
                  onClear={() => {
                    setSelectedFile(null);
                    setFormData(null);
                  }}
                />
                <HStack justify="between" align="center">
                  <TemplateDownloadButton />
                  <Text variant="caption" color={colors.text.tertiary}>
                    {t('leadImport.maxRows')}
                  </Text>
                </HStack>
                {uploadFile.error && (
                  <Text variant="caption" color={colors.status.error.main}>
                    {uploadFile.error.message}
                  </Text>
                )}
              </VStack>
            )}

            {step === 'preview' && (
              <VStack spacing="lg">
                <Text variant="body" color={colors.text.tertiary}>
                  {t('leadImport.totalRowsPreview', {
                    total: totalRows,
                    preview: Math.min(previewRows.length, 5),
                  })}
                </Text>
                <ImportPreviewTable headers={headers} rows={previewRows} mappings={mappings} />
                {executeImport.error && (
                  <Text variant="caption" color={colors.status.error.main}>
                    {executeImport.error.message}
                  </Text>
                )}
              </VStack>
            )}

            {step === 'importing' && (
              <ImportProgressCard totalRows={totalRows} isPending={executeImport.isPending} />
            )}

            {step === 'complete' && importResult && <ImportSummaryCard result={importResult} />}
          </ScrollView>
        )}

        {step !== 'importing' && (
          <View style={styles.footer}>
            {step === 'upload' && (
              <Button
                testID="upload_next_button"
                variant="primary"
                fullWidth
                onPress={handleUpload}
                disabled={!selectedFile}
                loading={uploadFile.isPending}
              >
                <HStack spacing="sm" align="center">
                  <Upload size={16} color={colors.background.primary} />
                  <Text variant="body" color={colors.background.primary}>
                    {t('leadImport.uploadAndAnalyze')}
                  </Text>
                </HStack>
              </Button>
            )}

            {step === 'mapping' && (
              <Button
                testID="mapping_next_button"
                variant="primary"
                fullWidth
                onPress={() => setStep('preview')}
              >
                <HStack spacing="sm" align="center">
                  <Text variant="body" color={colors.background.primary}>
                    {t('leadImport.preview')}
                  </Text>
                  <ArrowRight size={16} color={colors.background.primary} />
                </HStack>
              </Button>
            )}

            {step === 'preview' && (
              <VStack spacing="sm">
                {!activeTemplate && (
                  <Text variant="caption" color={colors.status.error.main}>
                    {t('leadImport.noActiveTemplate')}
                  </Text>
                )}
                <Button
                  testID="execute_import_button"
                  variant="primary"
                  fullWidth
                  onPress={handleExecuteImport}
                  loading={executeImport.isPending}
                  disabled={!activeTemplate}
                >
                  <HStack spacing="sm" align="center">
                    <Upload size={16} color={colors.background.primary} />
                    <Text variant="body" color={colors.background.primary}>
                      {t('leadImport.importNLeads', { n: totalRows })}
                    </Text>
                  </HStack>
                </Button>
              </VStack>
            )}

            {step === 'complete' && (
              <Button
                testID="close_summary_button"
                variant="primary"
                fullWidth
                onPress={handleClose}
              >
                <Text variant="body" color={colors.background.primary}>
                  {t('leadImport.done')}
                </Text>
              </Button>
            )}
          </View>
        )}
      </View>
    </Sheet>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    padding: tokens.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border.muted,
  },
  body: {
    flex: 1,
  },
  bodyContent: {
    padding: tokens.spacing.lg,
  },
  bodyNoClip: {
    flex: 1,
    padding: tokens.spacing.lg,
  },
  footer: {
    padding: tokens.spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.border.muted,
  },
});
